// How each client is told about this server. The differences are real — one
// takes a command, one takes an http entry, one can only speak stdio and needs
// a bridge — and getting them wrong costs someone an evening, so each snippet
// is written out rather than generated from a single guessed shape.
//
// The address alone is the normal case now: a client that meets it without
// credentials is sent to sign in, and the person approves in the product. A
// service token stays for the places where no browser will ever open — a cron
// job, a server, a CI step — and every snippet takes one when it is there.
export type McpClient = {
  // A label for the snippet below it: usually the file it goes in, sometimes
  // the command it is. Which one depends on whether a token is involved, so it
  // is asked the same question the snippet is.
  configLabel(input: { token: string | null }): string;
  id: string;
  name: string;
  snippet(input: { token: string | null; url: string }): string;
  // How many numbered steps this client needs; the text of each lives in the
  // dictionaries under mcpClients.<id>.
  stepCount: number;
};

const serverName = "impacto";

// Written as a header entry only when there is a token to put in it.
function headers(token: string | null) {
  return token ? { headers: { Authorization: `Bearer ${token}` } } : {};
}

export const mcpClients: McpClient[] = [
  {
    configLabel: () => "claude mcp add",
    id: "claude-code",
    name: "Claude Code",
    snippet: ({ token, url }) =>
      `claude mcp add --scope user --transport http ${serverName} ${url}${token ? ` --header "Authorization: Bearer ${token}"` : ""}`,
    stepCount: 3,
  },
  {
    configLabel: () => "claude_desktop_config.json",
    id: "claude-desktop",
    name: "Claude Desktop",
    snippet: ({ token, url }) =>
      JSON.stringify(
        {
          mcpServers: {
            [serverName]: {
              args: token ? ["-y", "mcp-remote", url, "--header", `Authorization: Bearer ${token}`] : ["-y", "mcp-remote", url],
              command: "npx",
            },
          },
        },
        null,
        2,
      ),
    stepCount: 3,
  },
  {
    // Codex speaks streamable HTTP natively and finds the sign-in flow by
    // itself, so with no token there is nothing to configure but the address.
    configLabel: ({ token }) => (token ? "~/.codex/config.toml" : "codex mcp add"),
    id: "codex",
    name: "Codex",
    snippet: ({ token, url }) =>
      token
        ? `[mcp_servers.${serverName}]\nurl = "${url}"\nhttp_headers = { Authorization = "Bearer ${token}" }`
        : `codex mcp add ${serverName} --url ${url}`,
    stepCount: 3,
  },
  {
    configLabel: () => "~/.cursor/mcp.json",
    id: "cursor",
    name: "Cursor",
    snippet: ({ token, url }) =>
      JSON.stringify({ mcpServers: { [serverName]: { ...headers(token), url } } }, null, 2),
    stepCount: 3,
  },
  {
    configLabel: () => ".vscode/mcp.json",
    id: "vscode",
    name: "VS Code",
    snippet: ({ token, url }) =>
      JSON.stringify({ servers: { [serverName]: { ...headers(token), type: "http", url } } }, null, 2),
    stepCount: 3,
  },
  {
    configLabel: () => "mcp_config.json",
    id: "windsurf",
    name: "Windsurf",
    snippet: ({ token, url }) =>
      JSON.stringify({ mcpServers: { [serverName]: { ...headers(token), serverUrl: url } } }, null, 2),
    stepCount: 3,
  },
  {
    configLabel: () => "Endpoint and header",
    id: "other",
    name: "Other client",
    snippet: ({ token, url }) => (token ? `${url}\nAuthorization: Bearer ${token}` : url),
    stepCount: 3,
  },
];

// Worth pasting into a fresh session: each one exercises a different part of
// the surface, and all of them are read-only. They are translated too — an
// assistant answers in the language it is asked in.
export const starterPromptKeys = ["describe", "blocking", "changed"];

// Installing the whole plugin — skills and the connection together — rather
// than only pointing a client at the MCP endpoint.
//
// Where a client registers a URL scheme with the operating system, this is a
// real button: one click hands the address over and the client takes it from
// there. Where it does not, the honest version is one command, ready to paste,
// rather than a page of instructions to follow by hand.
export type PluginTarget = {
  // The other way round, where there is one worth offering.
  alternative?(input: { origin: string }): { commands: string[]; key: string };
  // One line that does the whole thing, for a terminal.
  command(input: { origin: string }): string;
  // A link the operating system hands to the client, when there is one.
  deepLink?(input: { origin: string }): string;
  id: string;
  name: string;
  // The same work split up, so it can be read before it is run.
  steps(input: { origin: string }): string[];
};

// Unpacking the bundle into the skills directory is the install that works on
// every Claude Code there has ever been: no marketplace, no install step, no
// version to check — the directory is read at startup and the plugin is simply
// there. The marketplace route is better when it works (it updates itself), so
// it stays on offer, with the version it needs said out loud.
// Скачать и распаковать — это выполнить чужой файл: содержимое архива читает
// ассистент при каждом старте. Поэтому между скачиванием и распаковкой стоит
// сверка с опубликованной контрольной суммой, и цепочка через `&&` падает
// раньше, чем что-то попадёт в каталог скиллов.
const verifiedUnzip = (origin: string, archive: string, tmp: string, target: string) =>
  `curl -fsSL ${origin}/plugin/${archive} -o ${tmp} && curl -fsSL ${origin}/plugin/${archive}.sha256 | sed "s|  .*|  ${tmp}|" | shasum -a 256 -c - && unzip -o ${tmp} -d ${target}`;

const claudeSkillsInstall = (origin: string) =>
  verifiedUnzip(origin, "impacto.zip", "/tmp/impacto.zip", `~/.claude/skills/${serverName}`);

export const pluginTargets: PluginTarget[] = [
  {
    id: "claude-code",
    name: "Claude Code",
    // Opens a Claude Code session with the install already typed in; nothing
    // runs until the person reads it and presses Enter.
    deepLink: ({ origin }) =>
      `claude-cli://open?q=${encodeURIComponent(
        `Install the Impacto plugin for this workspace: run \`${claudeSkillsInstall(origin)}\`, then run \`claude plugin list\` and tell me whether impacto is loaded.`,
      )}`,
    command: ({ origin }) => claudeSkillsInstall(origin),
    steps: ({ origin }) => [claudeSkillsInstall(origin)],
    alternative: ({ origin }) => ({
      key: "claudeMarketplace",
      commands: [
        `claude plugin marketplace add ${origin}/plugin/marketplace.json`,
        `claude plugin install ${serverName}@${serverName}`,
      ],
    }),
  },
  {
    id: "codex",
    name: "Codex",
    command: ({ origin }) =>
      `codex mcp add ${serverName} --url ${origin}/mcp && ${verifiedUnzip(origin, "skills.zip", "/tmp/impacto-skills.zip", "~/.agents/skills")}`,
    steps: ({ origin }) => [
      `codex mcp add ${serverName} --url ${origin}/mcp`,
      verifiedUnzip(origin, "skills.zip", "/tmp/impacto-skills.zip", "~/.agents/skills"),
    ],
  },
  {
    id: "vscode",
    name: "VS Code",
    // VS Code takes the server entry as url-encoded JSON on its own scheme.
    deepLink: ({ origin }) =>
      `vscode:mcp/install?${encodeURIComponent(JSON.stringify({ name: serverName, type: "http", url: `${origin}/mcp` }))}`,
    command: ({ origin }) => `code --add-mcp '${JSON.stringify({ name: serverName, type: "http", url: `${origin}/mcp` })}'`,
    steps: ({ origin }) => [`code --add-mcp '${JSON.stringify({ name: serverName, type: "http", url: `${origin}/mcp` })}'`],
  },
  {
    id: "cursor",
    name: "Cursor",
    // Cursor takes the same thing base64-encoded, under its deeplink extension.
    deepLink: ({ origin }) =>
      `cursor://anysphere.cursor-deeplink/mcp/install?name=${serverName}&config=${btoa(JSON.stringify({ url: `${origin}/mcp` }))}`,
    command: ({ origin }) => JSON.stringify({ mcpServers: { [serverName]: { url: `${origin}/mcp` } } }, null, 2),
    steps: ({ origin }) => [JSON.stringify({ mcpServers: { [serverName]: { url: `${origin}/mcp` } } }, null, 2)],
  },
];
